import { alpha, useTheme } from "@mui/material";
import { useState } from "react";
import shortid from "shortid";
import Chip from "../chip/chip.component";
import Divider from "../divider/divider.component";
import Grid from "../grid/grid.component";
import Tooltip from "../tooltip/tooltip.component";

const situacoesVencimento = [
  {
    valor: "VENCIDAS",
    label: "Vencidas",
    cor: "#B3261E",
    icone: "ph-bold ph-alarm",
    statusPadrao: ["ABERTO", "BAIXADO_PARCIAL"],
  },
  {
    valor: "A_VENCER",
    label: "Não vencidas",
    cor: "#0F6C60",
    icone: "ph-bold ph-calendar-check",
  },
];

const situacaoVencimentoNeutra = {
  valor: null,
  label: "Vencimento: Todos",
  cor: "#6C757D",
  icone: "ph-bold ph-calendar-blank",
};

const ListStatusVencimento = ({
  selectedStatus,
  setSelectedStatus,
  listaStatus,
  handleChangeStatus,
  filtrosAvancados,
  setFiltrosAvancados,
}) => {
  const theme = useTheme();
  const [statusAutomaticos, setStatusAutomaticos] = useState([]);

  const situacaoVencimentoAtual = filtrosAvancados?.situacaoVencimento ?? null;

  const renderLabel = (status) => {
    if (status.status) {
      const textoMinusculo = status.status.toLowerCase();
      return textoMinusculo.charAt(0).toUpperCase() + textoMinusculo.slice(1);
    } else {
      return status.label;
    }
  };

  const nomeStatus = (status) => status.nome || status.status;

  const validateStatus = (status) => {
    const texto = nomeStatus(status);
    return selectedStatus.find((item) => item.nome === texto);
  };

  const handleClick = (status) => {
    const texto = nomeStatus(status);
    const newStatus = validateStatus(status)
      ? selectedStatus.filter((item) => item.nome !== texto)
      : [...selectedStatus, { id: status.id, nome: texto }];

    setStatusAutomaticos(statusAutomaticos.filter((item) => item !== texto));
    setSelectedStatus(newStatus);
    handleChangeStatus(newStatus, { ...filtrosAvancados });
  };

  const handleClickVencimento = () => {
    const indiceAtual = situacoesVencimento.findIndex(
      (item) => item.valor === situacaoVencimentoAtual,
    );
    const proximaSituacao = situacoesVencimento[indiceAtual + 1];
    const filtrosTemp = { ...filtrosAvancados };
    if (proximaSituacao) {
      filtrosTemp.situacaoVencimento = proximaSituacao.valor;
    } else {
      delete filtrosTemp.situacaoVencimento;
    }
    const statusFaltantes = listaStatus.filter(
      (status) =>
        proximaSituacao?.statusPadrao?.includes(nomeStatus(status)) &&
        !validateStatus(status),
    );
    const novoStatus = proximaSituacao
      ? [
          ...selectedStatus,
          ...statusFaltantes.map((status) => ({
            id: status.id,
            nome: nomeStatus(status),
          })),
        ]
      : selectedStatus.filter((item) => !statusAutomaticos.includes(item.nome));

    setStatusAutomaticos(
      proximaSituacao
        ? [
            ...statusAutomaticos,
            ...statusFaltantes.map((status) => nomeStatus(status)),
          ]
        : [],
    );
    setSelectedStatus(novoStatus);
    setFiltrosAvancados(filtrosTemp);
    handleChangeStatus(novoStatus, filtrosTemp);
  };

  const situacaoVencimento =
    situacoesVencimento.find(
      (item) => item.valor === situacaoVencimentoAtual,
    ) ?? situacaoVencimentoNeutra;

  const vencimentoSelecionado = !!situacaoVencimento.valor;

  const backgroundColorVencimento = vencimentoSelecionado
    ? situacaoVencimento.cor
    : alpha(situacaoVencimento.cor, 0.08);

  const textColorVencimento = vencimentoSelecionado
    ? theme.palette.getContrastText(backgroundColorVencimento)
    : "#000";

  const textHoverColorVencimento = vencimentoSelecionado
    ? "#000"
    : theme.palette.getContrastText(backgroundColorVencimento);

  return (
    <Grid container spacing={2}>
      {listaStatus.map((status) => {
        const isSelected = validateStatus(status);
        const backgroundColor = isSelected
          ? status.cor
          : alpha(status.cor, 0.08);

        const textColor = isSelected
          ? theme.palette.getContrastText(backgroundColor)
          : "#000";

        const textHoverColor = isSelected
          ? "#000"
          : theme.palette.getContrastText(backgroundColor);

        return (
          <Chip
            className="d-flex my-1 mx-2 p-1"
            key={shortid.generate()}
            icon={<i className="ph-fill ph-circle"></i>}
            label={renderLabel(status)}
            sx={{
              backgroundColor,
              color: textColor,
              "&:hover": {
                backgroundColor: isSelected
                  ? alpha(status.cor, 0.08)
                  : alpha(status.cor, 0.75),
                color: textHoverColor,
                transform: "scale(1.05)",
                transition: "all 0.4s ease-in-out",
              },
              ".MuiChip-icon": {
                color: isSelected ? "#fff" : status.cor,
              },
              "&:hover .MuiChip-icon": {
                color: isSelected ? status.cor : "#fff",
                transition: "all 0.4s ease-in-out",
              },
            }}
            onClick={() => handleClick(status)}
          />
        );
      })}
      <Divider
        orientation="vertical"
        flexItem
        sx={{ my: 1.5, border: "1px solid grey" }}
      />
      <Tooltip
        title="Clique para alternar entre vencidas, não vencidas e todas"
        arrow
        placement="top"
        className="d-flex align-items-center"
      >
        <Chip
          className="d-flex my-1 mx-2 p-1"
          icon={<i className={situacaoVencimento.icone}></i>}
          label={situacaoVencimento.label}
          sx={{
            backgroundColor: backgroundColorVencimento,
            color: textColorVencimento,
            "&:hover": {
              backgroundColor: vencimentoSelecionado
                ? alpha(situacaoVencimento.cor, 0.08)
                : alpha(situacaoVencimento.cor, 0.75),
              color: textHoverColorVencimento,
              transform: "scale(1.05)",
              transition: "all 0.4s ease-in-out",
            },
            ".MuiChip-icon": {
              color: vencimentoSelecionado ? "#fff" : situacaoVencimento.cor,
            },
            "&:hover .MuiChip-icon": {
              color: vencimentoSelecionado ? situacaoVencimento.cor : "#fff",
              transition: "all 0.4s ease-in-out",
            },
          }}
          onClick={handleClickVencimento}
        />
      </Tooltip>
    </Grid>
  );
};

export default ListStatusVencimento;
